import React, { useState, useEffect, useRef } from 'react';
import type { GuestAdmissionState } from './useKnockingState';

interface WaitingRoomProps {
  state: GuestAdmissionState;
  hostOnline: boolean;
  knockExpiresAt: string | null;
  displayName?: string;
  onDisplayNameChange?: (name: string) => void;
  email?: string;
  onEmailChange?: (email: string) => void;
  phone?: string;
  onPhoneChange?: (phone: string) => void;
  roomId?: string;
  onKnock: () => void;
  onCancel: () => void;
  onReKnock: () => void;
}

export function KnockingWaitingRoom({
  state,
  hostOnline,
  displayName = '',
  onDisplayNameChange,
  email = '',
  onEmailChange,
  phone = '',
  onPhoneChange,
  roomId = 'private',
  onKnock,
  onCancel,
  onReKnock,
}: WaitingRoomProps) {
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<string>('');
  const [selectedAudio, setSelectedAudio] = useState<string>('');
  const [micLevel, setMicLevel] = useState<number>(0);
  const [isCameraOn, setIsCameraOn] = useState<boolean>(true);
  const [isMicOn, setIsMicOn] = useState<boolean>(true);
  const [showAvModal, setShowAvModal] = useState<boolean>(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animFrameRef = useRef<number | null>(null);

  useEffect(() => {
    async function setupPreview() {
      try {
        if (!navigator?.mediaDevices?.getUserMedia) return;
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;

        const devices = await navigator.mediaDevices.enumerateDevices();
        const vInputs = devices.filter((d) => d.kind === 'videoinput');
        const aInputs = devices.filter((d) => d.kind === 'audioinput');
        setVideoDevices(vInputs);
        setAudioDevices(aInputs);
        if (vInputs.length > 0) setSelectedVideo(vInputs[0].deviceId);
        if (aInputs.length > 0) setSelectedAudio(aInputs[0].deviceId);

        const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
        if (AudioCtx) {
          const audioCtx = new AudioCtx();
          const analyser = audioCtx.createAnalyser();
          analyser.fftSize = 64;
          const source = audioCtx.createMediaStreamSource(stream);
          source.connect(analyser);

          const dataArray = new Uint8Array(analyser.frequencyBinCount);
          const checkAudio = () => {
            analyser.getByteFrequencyData(dataArray);
            let sum = 0;
            for (let i = 0; i < dataArray.length; i++) sum += dataArray[i];
            setMicLevel(Math.min(100, Math.round((sum / dataArray.length) * 1.5)));
            animFrameRef.current = requestAnimationFrame(checkAudio);
          };
          checkAudio();
        }
      } catch (err) {
        console.warn('[WaitingRoom] Media device access unavailable:', err);
      }
    }

    if (state === 'preview') setupPreview();

    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [state]);

  function toggleCamera() {
    if (streamRef.current) {
      const track = streamRef.current.getVideoTracks()[0];
      if (track) {
        track.enabled = !track.enabled;
        setIsCameraOn(track.enabled);
      }
    }
  }

  function toggleMic() {
    if (streamRef.current) {
      const track = streamRef.current.getAudioTracks()[0];
      if (track) {
        track.enabled = !track.enabled;
        setIsMicOn(track.enabled);
      }
    }
  }

  // --- TERMINAL & WAITING STATES ---

  if (state === 'denied') {
    return (
      <div className="h-screen w-screen flex items-center justify-center p-4 bg-neutral-950 text-white">
        <div className="max-w-sm w-full p-6 bg-neutral-900 border border-neutral-800 rounded-2xl text-center space-y-4">
          <div className="w-12 h-12 mx-auto rounded-full bg-red-950/60 border border-red-800 flex items-center justify-center text-red-400 font-bold text-xl">✕</div>
          <h3 className="text-lg font-bold">Entry Declined</h3>
          <p className="text-xs text-neutral-400">The room host declined your admission request to #{roomId}.</p>
          <button onClick={onCancel} className="w-full py-2 text-xs font-semibold bg-neutral-800 hover:bg-neutral-700 rounded-xl">Exit</button>
        </div>
      </div>
    );
  }

  if (state === 'pending_admission' || state === 'host_away') {
    return (
      <div className="h-screen w-screen flex items-center justify-center p-4 bg-neutral-950 text-white">
        <div className="max-w-sm w-full p-6 bg-neutral-900 border border-neutral-800 rounded-2xl text-center space-y-4">
          <div className="relative w-12 h-12 mx-auto">
            <div className="absolute inset-0 rounded-full border-2 border-cyan-500/20" />
            <div className="absolute inset-0 rounded-full border-2 border-cyan-400 border-t-transparent animate-spin" />
          </div>
          <h3 className="text-base font-bold">{state === 'host_away' ? 'Host Away' : 'Waiting for Host Approval'}</h3>
          <p className="text-xs text-neutral-400">
            Knocked on <span className="text-cyan-400 font-mono">#{roomId}</span> as <strong className="text-white">{displayName || 'Guest'}</strong>.
          </p>
          <button onClick={onCancel} className="py-2 px-5 text-xs font-semibold bg-neutral-800 hover:bg-neutral-700 rounded-xl text-neutral-300">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // --- ULTRA-LEAN PREFLIGHT CARD (ZERO OVERLAP) ---

  return (
    <div className="h-screen w-screen bg-neutral-950 text-white flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-neutral-900 border border-neutral-800 rounded-2xl p-5 shadow-2xl space-y-4">
        
        {/* Header */}
        <div className="flex items-center justify-between border-b border-neutral-800 pb-3">
          <div>
            <h2 className="text-base font-bold">Join Private Room</h2>
            <p className="text-[11px] text-neutral-400">Doorbell-gated guest access</p>
          </div>
          <span className="text-xs font-mono px-2.5 py-0.5 rounded-full bg-emerald-950 text-emerald-400 border border-emerald-800 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            #{roomId}
          </span>
        </div>

        {/* Display Name Input (Primary & Unobstructed) */}
        <div className="space-y-1">
          <label className="block text-[11px] font-mono uppercase tracking-wider text-neutral-400">
            Your Display Name
          </label>
          <input
            type="text"
            placeholder="e.g. Alex"
            value={displayName}
            onChange={(e) => onDisplayNameChange && onDisplayNameChange(e.target.value)}
            className="w-full px-3 py-2 text-sm bg-neutral-950 border border-neutral-700 focus:border-cyan-400 rounded-xl text-white placeholder-neutral-600 focus:outline-none"
            autoFocus
          />
        </div>

        {/* Contact Info (Optional for Host Follow-up / Reconnect) */}
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <label className="block text-[10px] font-mono uppercase tracking-wider text-neutral-400">
              Email (Optional)
            </label>
            <input
              type="email"
              placeholder="alex@domain.com"
              value={email}
              onChange={(e) => onEmailChange && onEmailChange(e.target.value)}
              className="w-full px-2.5 py-1.5 text-xs bg-neutral-950 border border-neutral-700 focus:border-cyan-400 rounded-xl text-white placeholder-neutral-600 focus:outline-none"
            />
          </div>
          <div className="space-y-1">
            <label className="block text-[10px] font-mono uppercase tracking-wider text-neutral-400">
              WhatsApp / Phone
            </label>
            <input
              type="tel"
              placeholder="+1 555-0199"
              value={phone}
              onChange={(e) => onPhoneChange && onPhoneChange(e.target.value)}
              className="w-full px-2.5 py-1.5 text-xs bg-neutral-950 border border-neutral-700 focus:border-cyan-400 rounded-xl text-white placeholder-neutral-600 focus:outline-none"
            />
          </div>
        </div>

        {/* MICRO-ICON MEDIA PILL (Takes only 28px height) */}
        <div className="flex items-center justify-between px-3 py-2 bg-neutral-950/80 rounded-xl border border-neutral-800 text-xs">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${isMicOn ? 'bg-emerald-400' : 'bg-red-400'}`} />
            <span className="text-[11px] text-neutral-300">
              {isMicOn ? `Mic Active (${micLevel}%)` : 'Mic Muted'}
            </span>
          </div>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={toggleMic}
              className={`p-1 text-[11px] rounded ${isMicOn ? 'bg-neutral-800 text-neutral-300' : 'bg-red-950 text-red-300'}`}
              title="Toggle Mic"
            >
              🎙️
            </button>
            <button
              type="button"
              onClick={toggleCamera}
              className={`p-1 text-[11px] rounded ${isCameraOn ? 'bg-neutral-800 text-neutral-300' : 'bg-red-950 text-red-300'}`}
              title="Toggle Camera"
            >
              📷
            </button>
            <button
              type="button"
              onClick={() => setShowAvModal(true)}
              className="px-2 py-0.5 text-[11px] bg-neutral-800 hover:bg-neutral-700 text-neutral-300 rounded transition"
            >
              ⚙️ Video
            </button>
          </div>
        </div>

        {/* Host Status & Knock Trigger */}
        <div className="pt-1 space-y-2">
          <button
            type="button"
            onClick={onKnock}
            className="w-full py-2.5 px-4 text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 active:scale-[0.99] text-white rounded-xl shadow-lg transition"
          >
            Knock to Request Entry
          </button>
          <div className="flex items-center justify-center gap-1.5 text-[11px] text-neutral-500">
            <span className={`w-2 h-2 rounded-full ${hostOnline ? 'bg-emerald-400 animate-pulse' : 'bg-neutral-600'}`} />
            <span>{hostOnline ? 'Host is currently inside' : 'Host is offline'}</span>
          </div>
        </div>

      </div>

      {/* OPTIONAL EXPANDABLE AV PREVIEW MODAL */}
      {showAvModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-sm bg-neutral-900 border border-neutral-800 rounded-2xl p-5 space-y-4">
            <div className="flex items-center justify-between border-b border-neutral-800 pb-2">
              <span className="text-xs font-bold text-white">Camera & Audio Preview</span>
              <button onClick={() => setShowAvModal(false)} className="text-neutral-400 hover:text-white text-sm font-bold">✕</button>
            </div>
            <div className="relative aspect-video bg-black rounded-lg overflow-hidden border border-neutral-800 flex items-center justify-center">
              <video ref={videoRef} autoPlay muted playsInline className={`w-full h-full object-cover transform -scale-x-100 ${!isCameraOn ? 'hidden' : ''}`} />
              {!isCameraOn && <span className="text-xs text-neutral-500">Camera Off</span>}
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <select value={selectedVideo} onChange={(e) => setSelectedVideo(e.target.value)} className="bg-neutral-950 p-1.5 rounded border border-neutral-800 text-neutral-300 truncate">
                {videoDevices.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || 'Camera'}</option>)}
              </select>
              <select value={selectedAudio} onChange={(e) => setSelectedAudio(e.target.value)} className="bg-neutral-950 p-1.5 rounded border border-neutral-800 text-neutral-300 truncate">
                {audioDevices.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || 'Mic'}</option>)}
              </select>
            </div>
            <button onClick={() => setShowAvModal(false)} className="w-full py-1.5 bg-neutral-800 hover:bg-neutral-700 text-xs text-neutral-200 rounded-lg">Done</button>
          </div>
        </div>
      )}
    </div>
  );
}
